import "./PhysicianDetailsFormComponent.scss";
import {Field, FieldProps, Form, Formik, FormikHelpers, FormikProps, FormikValues} from "formik";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import HeadingComponent from "../heading/headingComponent";
import FormikInputComponent from "../core/formik-input/FormikInputComponent";
import FormikRadioButtonGroupComponent from "../core/formik-radio-button-group/formikRadioButtonGroupComponent";
import {CommonService} from "../../services";
import FormikAutoCompleteDropdown from "../core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import {APIConfig} from "../../../constants";
import ButtonComponent from "../core/button/ButtonComponent";
import * as Yup from "yup";
import _ from "lodash";
import {IAPIResponseType} from "../../models/api.model";
import FromikPhoneNumberComponent from "../core/fromik-phone-number/FromikPhoneNumberComponent";
import commonService from "../../services/common.service";
import FormikSelectDropdownComponent from "../core/formik-select-dropdown/FormikSelectDropdownComponent";
import useFetch from "../../hooks/useFetch";
import FeedbackComponent from "../feedback/feedbackComponent";

interface PhysicianDetailsFormComponentProps {
    formInitialValues?: any;
    onUpdateComplete?: Function;
    facilityID?: any;
    physicianId?: any;
    physicianMode?: 'edit' | 'add';
    physicianList: any[];
    isNpiClearable?: boolean;
}

const addPhysicianValidationSchema = Yup.object({
    id: Yup.string(),
    npiNumber: Yup.string().matches(/^[0-9]{10}$/, 'NPI Number must be 10 digits')
        .required('NPI Number is a required field'),
    designation: Yup.string().nullable(),
    otherDesignation: Yup.string().when('designation', {
        is: (designation: string) => designation === 'other',
        then: Yup.string().nullable()
        // .required('designation is required'),
    }),
    prefix: Yup.string().nullable(),
    suffix: Yup.string().nullable(),
    firstName: Yup.string()
        .required("First Name  is required")
        .max(100, 'First name maximum limit should be 100 characters'),
    middleName: Yup.string()
        .max(100, 'Middle name maximum limit should be 100 characters').nullable(),
    lastName: Yup.string()
        .required("Last Name  is required")
        .max(100, 'Last name maximum limit should be 100 characters'),
    gender: Yup.string().nullable(),
    taxonomyDetails: Yup.object({
        code: Yup.string().nullable(),
        group: Yup.string().nullable(),
        desc: Yup.string().nullable(),
        state: Yup.string().nullable(),
        license: Yup.string().nullable(),
        primary: Yup.boolean().nullable(),
    }).nullable(),
    specialityType: Yup.string().nullable()
        .max(100, 'Speciality Type maximum limit should be 100 characters'),
    ptanNumber: Yup.string().nullable(),
    // .matches(/^[0-9]{6}$/, "Medicare PTAN Number should be 6 digits"),
    tinNumber: Yup.string().nullable(),
    // .matches(/^[0-9]{9}$/, "Medicaid TIN Number should be 9 digits"),
    emailId: Yup.string().nullable()
        .email("Invalid Email ID")
        .required("Email ID is required")
        .matches(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/, "Enter a valid Email ID"),
    mobileNumber: Yup.string().nullable()
        // .required("Mobile Number is required")
        .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits"),
    faxNumber: Yup.string().nullable().matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Fax Number should be 10 digits"),
    isAddressSameAsFacility: Yup.boolean().nullable(),
    addressLine1: Yup.string().when('isAddressSameAsFacility', {
        is: (isAddressSameAsFacility: boolean) => !isAddressSameAsFacility,
        then: Yup.string().nullable()
            // .required("Address 1 is required")
            .max(200, 'Address Line 1 cannot exceed 200 characters'),
    }).nullable(),
    addressLine2: Yup.string().when('isAddressSameAsFacility', {
        is: (isAddressSameAsFacility: boolean) => !isAddressSameAsFacility,
        then: Yup.string().nullable()
            .max(200, 'Address Line 2 cannot exceed 200 characters'),
    }).nullable(),
    zipcode: Yup.mixed().when('isAddressSameAsFacility', {
        is: (isAddressSameAsFacility: boolean) => !isAddressSameAsFacility,
        then: Yup.mixed().nullable()
        // .required("ZIP Code is required"),
    }).nullable(),
    state: Yup.mixed().when('isAddressSameAsFacility', {
        is: (isAddressSameAsFacility: boolean) => !isAddressSameAsFacility,
        then: Yup.mixed().nullable()
        // .required("State is required"),
    }).nullable(),
    city: Yup.mixed().when('isAddressSameAsFacility', {
        is: (isAddressSameAsFacility: boolean) => !isAddressSameAsFacility,
        then: Yup.mixed().nullable()
        // .required("City is required"),
    }).nullable(),
    newZipCode: Yup.string().when('isAddressSameAsFacility', {
        is: (isAddressSameAsFacility: boolean) => !isAddressSameAsFacility,
        then: Yup.string().nullable().when('zipcode', {
            is: (zipcode: any) => (zipcode?.zipcode && zipcode?.zipcode?.toLowerCase() === "other"),
            then: Yup.string()
                // .required('ZIP Code is required')
                .matches(/^[0-9]{5}$/, "Enter a valid ZIP Code"),
        })
    }).nullable(),
})

interface addPhysicianInformationFormObject {
    id: string;
    npiNumber: string;
    designation: string;
    otherDesignation: string;
    prefix: string;
    suffix: string;
    firstName: string;
    middleName: string;
    lastName: string;
    gender: string;
    specialityType: string;
    ptanNumber: string;
    tinNumber: string;
    taxonomyDetails: {
        code: string,
        group: string,
        desc: string,
        state: string,
        license: string,
        primary: boolean
    }
    emailId: string;
    mobileNumber: string;
    faxNumber: string;
    isAddressSameAsFacility: boolean;
    addressLine1: string;
    addressLine2: string;
    zipcode: any;
    state: string;
    city: string;
    newZipCode: string;
    isPhysicianExpand: boolean;
}

const physicianDetailsFormInitialValues: addPhysicianInformationFormObject = {
    id: "",
    npiNumber: "",
    designation: "md",
    otherDesignation: "",
    prefix: "",
    suffix: "",
    firstName: "",
    middleName: "",
    lastName: "",
    gender: "",
    specialityType: "",
    ptanNumber: "",
    tinNumber: "",
    mobileNumber: "",
    faxNumber: "",
    emailId: "",
    taxonomyDetails: {
        code: "",
        group: "",
        desc: "",
        state: "",
        license: "",
        primary: false
    },
    isAddressSameAsFacility: false,
    addressLine1: "",
    addressLine2: "",
    zipcode: {},
    newZipCode: "",
    state: "",
    city: "",
    isPhysicianExpand: false,
}

const isPhysicianFieldDisabled = (npiField: string, npiAlreadyExist: boolean, isFetched: boolean) => {
    // Condition 1: NPI not fetched & NPI text field populated --> disable form field
    if (!isFetched && npiField.length > 0) {
        return true;
    }
    // Condition 2: NPI fetched --> disable NPI edit field
    return isFetched;

}

const PhysicianDetailsFormComponent = (props: PhysicianDetailsFormComponentProps) => {
    const {
        formInitialValues,
        onUpdateComplete,
        facilityID,
        physicianMode,
        physicianList,
        isNpiClearable = true
    } = props;

    const [state, setState] = useState<string>("");
    const [isZipCodeNotFound, setIsZipCodeNotFound] = useState<boolean>(false);
    const [physicianDetailsMode] = useState<'add' | 'edit' | undefined>(physicianMode);
    const [addPhysicianFormInitialValues, setAddPhysicianFormInitialValues] = useState<addPhysicianInformationFormObject>(_.cloneDeep(physicianDetailsFormInitialValues));
    const [npiAlreadyExist, setNpiAlreadyExist] = useState<boolean>(false);
    const [isNpiFetched, setIsNpiFetched] = useState(false);
    const formikRef = useRef<FormikProps<FormikValues>>(null);

    const {
        response: physicianDetails,
        loading,
        fetchData
    } = useFetch<any>();
    const response = useMemo(() => formInitialValues || physicianDetails, [formInitialValues, physicianDetails])

    const {
        response: facilityResponse,
        fetchData: fetchFacilityData
    } = useFetch<any>();

    const getFacilityData = useCallback(() => {
        fetchFacilityData({
            url: APIConfig.GET_FACILITY_DETAILS.URL(facilityID),
            method: APIConfig.GET_FACILITY_DETAILS.METHOD,
            payload: {}
        })
    }, [facilityID, fetchFacilityData])

    useEffect(() => {
        if (facilityID) {
            getFacilityData();
        }
    }, [getFacilityData, facilityID]);

    const handleZipCodeValueChange = useCallback((zipCodeObject: any, setFieldValue: any) => {
        if (zipCodeObject === "") {
            setFieldValue("state", "");
            setFieldValue("city", "");
            // setFieldValue("timezone", "");
            setIsZipCodeNotFound(false);
            setState("");
        } else {
            if (zipCodeObject?.zipcode?.toLowerCase() === "other") {
                setIsZipCodeNotFound(true);
                setFieldValue("state", "");
                setFieldValue("city", "");
                // setFieldValue("timezone", "");
            } else {
                setIsZipCodeNotFound(false);
                setFieldValue("state", zipCodeObject.state);
                setFieldValue("city", zipCodeObject.city);
                // setFieldValue("timezone", zipCodeObject.timezone);
            }
        }
        setFieldValue("newZipCode", "");
    }, []);

    const editFacilityDetails = useCallback((physicianDetails: any) => {
        try {
            const payload = {
                physicians: [physicianDetails.id]
            }
            CommonService._facility.editFacilityDetails(physicianDetails.facilityId, payload)
                .then((response: IAPIResponseType<any>) => {
                    if (onUpdateComplete) {
                        onUpdateComplete(physicianDetails, physicianDetailsMode);
                        CommonService._alert.showToast(response?.message || 'Physician has been added', "success");
                    }
                })
                .catch((error: any) => {
                    CommonService._alert.showToast(error?.error || `Error adding physician to Facility`, "error");
                });
        } catch (error) {
            console.error("Something went wrong edit facility details", error)
        }
    }, [onUpdateComplete, physicianDetailsMode])

    const clearNpiDetails = useCallback(() => {
        setNpiAlreadyExist(false);
        setIsNpiFetched(false);
        setAddPhysicianFormInitialValues(physicianDetailsFormInitialValues);
    }, [])
    const getNpiVerification = useCallback((npiNumber: string | number) => {
        const npiNumberList = physicianList?.map((physician: any) => physician?.npiNumber?.toString());
        if (npiNumberList?.includes(npiNumber?.toString())) {
            CommonService._alert.showToast(`Physician already exists`, "error");
            return
        }
        fetchData({
            url: APIConfig.GET_NPI_VERIFICATION.URL(npiNumber),
            method: APIConfig.GET_NPI_VERIFICATION.METHOD,
            payload: {
                facilityId: facilityID
            }
        })
    }, [facilityID, fetchData, physicianList])
    useEffect(() => {
        if (response) {
            setIsNpiFetched(true);
            if (response.npiNumber) {
                setNpiAlreadyExist(true);
                const tempResponse = _.cloneDeep(response);
                tempResponse.isAddressSameAsFacility = response?.isSameAddress || false;
                tempResponse.designation = response?.designation || "md";
                setAddPhysicianFormInitialValues(tempResponse);
                //block to update the zip code to ui
                /*
                if (!tempResponse.zipcodeDetails) {
                    tempResponse.newZipCode = tempResponse?.zipcode;
                    tempResponse.zipcode = {
                        zipcode: ''
                    }
                    tempResponse.state = tempResponse?.stateDetails;
                    tempResponse.city = tempResponse?.cityDetails;
                    setIsZipCodeNotFound(true);
                } else {
                    tempResponse.zipcode = tempResponse.zipcodeDetails;
                    setIsZipCodeNotFound(false);
                }
 */
                tempResponse.zipcode = tempResponse.zipcodeDetails;
                setIsZipCodeNotFound(false);
                setState(tempResponse?.state);
                //end of zip code update
            }
        }
    }, [response]);

    const onPhysicianDetailsSubmit = useCallback((values: any, {
        setSubmitting,
        setErrors,
        validateForm
    }: FormikHelpers<any>) => {
        if (physicianList?.some(physician =>
            physician.npiNumber === values.npiNumber &&
            physician.firstName?.toLowerCase() === values.firstName?.toLowerCase() &&
            physician.lastName?.toLowerCase() === values.lastName?.toLowerCase()
        )) {
            CommonService._alert.showToast("Physician already exists in facility", "error");
            setSubmitting(false);
            return;
        }
        const payload = _.cloneDeep(values);
        payload.isSameAddress = payload.isAddressSameAsFacility;
        if (payload["newZipCode"]) {
            payload["zipcode"] = payload["newZipCode"];
            payload["state"] = payload?.state?.state;
            payload["city"] = payload?.city?.city;
            delete payload["newZipCode"];
        } else {
            payload["zipcode"] = payload?.zipcode?.zipcode || "";
        }
        if (!payload["zipcode"]) {
            delete payload["zipcodeDetails"]
        }
        if (!payload["city"]) {
            delete payload["cityDetails"]
        }
        if (payload['designation'] === 'other') {
            payload['designation'] = payload['otherDesignation'];
        }
        setSubmitting(true);
        // if (payload["isAddressSameAsFacility"]) {
        //     delete payload['addressLine1'];
        //     delete payload['addressLine2'];
        //     delete payload['zipcode'];
        //     delete payload['state'];
        //     delete payload['city'];
        //     delete payload['id'];
        // }
        if (payload["isAddressSameAsFacility"]) {
            const facilityAddressDetails = facilityResponse.addressDetails;
            payload['addressLine1'] = facilityAddressDetails?.addressLine1;
            payload['addressLine2'] = facilityAddressDetails?.addressLine2;
            payload['zipcode'] = facilityAddressDetails?.zipcode;
            payload['state'] = facilityAddressDetails?.state;
            payload['city'] = facilityAddressDetails?.city;
            delete payload['id'];
        }
        payload.mobileNumber = payload?.mobileNumber?.replace(/[^0-9]/g, '') || null;
        if (physicianDetailsMode === 'add') {
            CommonService._facility.addPhysicianInformation(facilityID, payload)
                .then((response: IAPIResponseType<any>) => {
                    if (response.data?.isPhyscisianAlreadyExist) {
                        editFacilityDetails(response.data)
                    } else {
                        if (onUpdateComplete) {
                            onUpdateComplete(response.data, physicianDetailsMode);
                            CommonService._alert.showToast(response?.message || 'Physician has been added', "success");
                        }
                    }
                    setSubmitting(false);
                })
                .catch((error: any) => {
                    CommonService._alert.showToast(error?.message?.error || 'Error adding Physician', "error");
                    CommonService.handleErrors(setErrors, error);
                    setSubmitting(false);
                })
        } else {
            CommonService._facility.editPhysicianInformation(facilityID, values?.id, payload)
                .then((response: IAPIResponseType<any>) => {
                    setSubmitting(false);
                    CommonService._alert.showToast(response?.message || 'Lab details has been edited', "success");
                    if (onUpdateComplete) {
                        onUpdateComplete(response.data);
                    }
                })
                .catch((error: any) => {
                    CommonService._alert.showToast(error?.message || 'Error adding Physician', "error");
                    CommonService.handleErrors(setErrors, error);
                    setSubmitting(false);
                })
        }
    }, [physicianDetailsMode, facilityID, editFacilityDetails, onUpdateComplete, physicianList, facilityResponse?.addressDetails]);

    useEffect(() => {
        if (physicianDetailsMode === 'edit') {
            const physicianDetailObject = _.cloneDeep(formInitialValues);
            physicianDetailObject.mobileNumber = commonService.getMaskedMobileNumber(physicianDetailObject?.mobileNumber);
            physicianDetailObject.secondaryMobileNumber = commonService.getMaskedMobileNumber(physicianDetailObject?.secondaryMobileNumber)
            if (formInitialValues.isAddressSameAsFacility) {
                delete physicianDetailObject['addressLine1'];
                delete physicianDetailObject['addressLine2'];
                delete physicianDetailObject['zipcode'];
                delete physicianDetailObject['state'];
                delete physicianDetailObject['city'];
            } else {
                if (!formInitialValues.zipcodeDetails) {
                    physicianDetailObject.newZipCode = physicianDetailObject?.zipcode;
                    physicianDetailObject.zipcode = {
                        zipcode: "Other"
                    };
                    physicianDetailObject.state = physicianDetailObject?.stateDetails;
                    physicianDetailObject.city = physicianDetailObject?.cityDetails;
                    setIsZipCodeNotFound(true);
                } else {
                    physicianDetailObject.zipcode = physicianDetailObject.zipcodeDetails;
                    setIsZipCodeNotFound(false);
                }
                setState(physicianDetailObject.state);
            }
            setAddPhysicianFormInitialValues(_.cloneDeep(physicianDetailObject));
        }
    }, [physicianDetailsMode, formInitialValues])

    return (
        <div className="add-physician-component screen t-form-screen">
            <div className={'t-form t-form-drawer'}>
                <Formik
                    validationSchema={addPhysicianValidationSchema}
                    initialValues={addPhysicianFormInitialValues}
                    enableReinitialize={true}
                    innerRef={formikRef}
                    onSubmit={onPhysicianDetailsSubmit}>
                    {({
                          isSubmitting,
                          values,
                          isValid,
                          touched,
                          resetForm,
                          setFieldValue,
                          validateForm,
                          errors
                      }) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [validateForm, values]);
                        return (
                            <Form noValidate={true} className={"add-physician-form-holder"}>
                                {/*{JSON.stringify(values)}*/}
                                <div className="form-header d-flex justify-content-space-between mrg-bottom-20">
                                    <HeadingComponent
                                        title={physicianDetailsMode === 'add' ? "Add Provider/Physician" : "Edit Provider/Physician"}/>
                                    {isNpiClearable &&
                                        <div
                                            className="deselect-content horizontal-vertical-center-align filters-clear-all-button"
                                            onClick={() => {
                                                resetForm()
                                                clearNpiDetails();
                                            }}>
                                            Clear All
                                        </div>
                                    }
                                </div>
                                <div className={'t-form-controls-wrapper physicians-form-controls-wrapper'}>
                                    <div className="ts-row">
                                        <div className="ts-col-8">
                                            <Field name={'npiNumber'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            required={true}
                                                            disabled={isNpiFetched}
                                                            label={'NPI Number'}
                                                            placeholder={'Enter 10-digit NPI Number'}
                                                            type={"text"}
                                                            maxLength={10}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                        <div className="ts-col-4">
                                            {isNpiFetched ? <ButtonComponent variant={"outlined"}
                                                                             handleClick={clearNpiDetails}
                                                                             disabled={!isNpiClearable}
                                                                             className={"mrg-top-5"}
                                                                             fullWidth={false}
                                                                             size={"large"}
                                                                             color={"secondary"}>
                                                Clear NPI
                                            </ButtonComponent> : <ButtonComponent variant={"outlined"}
                                                                                  handleClick={getNpiVerification.bind(this, values?.npiNumber)}
                                                                                  className={"mrg-top-5"}
                                                                                  fullWidth={false}
                                                                                  isLoading={loading}
                                                                                  disabled={!values.npiNumber}
                                                                                  size={"large"}
                                                                                  color={"secondary"}>
                                                Proceed
                                            </ButtonComponent>}
                                        </div>
                                    </div>
                                    {!isNpiFetched && <FeedbackComponent>
                                        If the provider/physician lacks an NPI (National Provider Identifier) number,
                                        please reach out to your designated administrator for assistance.
                                    </FeedbackComponent>}
                                    {isNpiFetched && <>
                                        <div className={'horizontal-line'}/>
                                        <div className="form-sub-title">Personal Details</div>
                                        <div className={'d-flex align-items-center'}>
                                            <div className='radio-button-title'>Provider/Physician is:
                                            </div>
                                            <div className={'mrg-left-20'}>
                                                <Field name={'designation'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikRadioButtonGroupComponent
                                                                direction={"row"}
                                                                formikField={field}
                                                                titleKey={'title'}
                                                                valueKey={'code'}
                                                                onUpdate={(value: any) => {
                                                                }}
                                                                options={CommonService._staticData.PhysicianMdOptions}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                        </div>
                                        {
                                            values.designation === 'other' &&
                                            <Field name={'otherDesignation'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Designation'}
                                                            placeholder={'Designation'}
                                                            fullWidth={true}
                                                            type={"text"}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        }
                                        <>
                                            <Field name={'prefix'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Prefix'}
                                                            placeholder={'Enter Prefix'}
                                                            type={"text"}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'suffix'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Suffix'}
                                                            placeholder={'Enter Suffix'}
                                                            type={"text"}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'firstName'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            disabled={response.firstName && isPhysicianFieldDisabled(values.npiNumber, npiAlreadyExist, isNpiFetched)}
                                                            label={'First Name'}
                                                            placeholder={'Enter First Name'}
                                                            type={"text"}
                                                            fullWidth={false}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'middleName'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            // disabled={isPhysicianFieldDisabled(values.npiNumber, npiAlreadyExist, isNpiFetched)}
                                                            label={'Middle Name'}
                                                            placeholder={'Enter Middle Name'}
                                                            type={"text"}
                                                            fullWidth={false}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'lastName'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            disabled={response.lastName && isPhysicianFieldDisabled(values.npiNumber, npiAlreadyExist, isNpiFetched)}
                                                            label={'Last Name'}
                                                            placeholder={'Enter Last Name'}
                                                            type={"text"}
                                                            required={true}
                                                            fullWidth={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field
                                                name={'gender'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikSelectDropdownComponent
                                                            label={'Gender'}
                                                            options={CommonService._staticData.genderOptions}
                                                            placeholder={'Select Gender'}
                                                            valueKey={'code'}
                                                            getOptionLabel={(option) => (option.title)}
                                                            formikField={field}/>
                                                    )
                                                }
                                            </Field>
                                            <Field name={'specialityType'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Speciality Type'}
                                                            placeholder={'Enter Speciality Type'}
                                                            fullWidth={false}
                                                            type={"text"}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'ptanNumber'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Medicare PTAN Number'}
                                                            placeholder={'Enter Medicare PTAN Number'}
                                                            type={"text"}
                                                            maxLength={6}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'tinNumber'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Medicaid TIN Number'}
                                                            placeholder={'Enter Medicaid TIN Number'}
                                                            type={"text"}
                                                            maxLength={9}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <div className={'horizontal-line'}/>
                                            <div className="form-sub-title">Taxonomy Details</div>
                                            <Field
                                                name={'taxonomyDetails.primary'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikSelectDropdownComponent
                                                            label={'Primary Taxonomy'}
                                                            options={CommonService._staticData.yesNoOptions}
                                                            placeholder={'Select Taxonomy'}
                                                            valueKey={'code'}
                                                            getOptionLabel={(option) => (option.title)}
                                                            formikField={field}/>
                                                    )
                                                }
                                            </Field>
                                            <Field name={'taxonomyDetails.code'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Taxonomy Code'}
                                                            placeholder={'Enter Taxonomy Code'}
                                                            type={"text"}
                                                            maxLength={9}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'taxonomyDetails.desc'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Descending Sequence'}
                                                            placeholder={'Enter Descending Sequence'}
                                                            type={"text"}
                                                            maxLength={9}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'taxonomyDetails.state'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'State'}
                                                            placeholder={'Enter State'}
                                                            type={"text"}
                                                            maxLength={9}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'taxonomyDetails.license'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'License Number'}
                                                            placeholder={'Enter License Number'}
                                                            type={"text"}
                                                            maxLength={9}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'taxonomyDetails.group'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Taxonomy Group'}
                                                            placeholder={'Enter Taxonomy Group'}
                                                            type={"text"}
                                                            maxLength={9}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>

                                            <div className={'horizontal-line'}/>
                                            <div className="form-sub-title">Contact Details</div>
                                            <Field name={'emailId'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            disabled={response.emailId && isPhysicianFieldDisabled(values.npiNumber, npiAlreadyExist, isNpiFetched)}
                                                            label={'Email ID'}
                                                            placeholder={'Enter Email ID'}
                                                            type={"email"}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'mobileNumber'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FromikPhoneNumberComponent
                                                            label={'Mobile Number'}
                                                            placeholder={'(000) 000-0000'}
                                                            maxLength={14}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'faxNumber'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FromikPhoneNumberComponent
                                                            label={'Fax Number'}
                                                            placeholder={'(000) 000-0000'}
                                                            maxLength={14}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <div className={'horizontal-line'}/>
                                            <div className="form-sub-title">Address Details</div>
                                            <div className={'d-flex align-items-center'}>
                                                <div className='physician-address-same-as-facility-header'>Same
                                                    Address
                                                    Details
                                                    as Facility?
                                                </div>
                                                <div className={'mrg-left-20'}>
                                                    <Field name={'isAddressSameAsFacility'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikRadioButtonGroupComponent
                                                                    direction={"row"}
                                                                    formikField={field}
                                                                    titleKey={'title'}
                                                                    valueKey={'code'}
                                                                    isValueBoolean={true}
                                                                    onUpdate={(value: any) => {
                                                                    }}
                                                                    options={CommonService._staticData.yesNoOptions}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                        </>
                                        {
                                            !values.isAddressSameAsFacility && <>
                                                <Field name={'addressLine1'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Address Line 1'}
                                                                placeholder={'Enter Address Line 2'}
                                                                type={"text"}
                                                                required={false}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                                <Field name={'addressLine2'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Address Line 2'}
                                                                placeholder={'Enter Address Line 1'}
                                                                type={"text"}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                                <Field name={'zipcode'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikAutoCompleteDropdown
                                                                label={'ZIP Code'}
                                                                placeholder={'Start typing for ZIP Code'}
                                                                required={false}
                                                                formikField={field}
                                                                fullWidth={true}
                                                                openOnFocus={true}
                                                                url={APIConfig.ZIPCODE_LIST.URL}
                                                                method={APIConfig.ZIPCODE_LIST.METHOD}
                                                                searchMode={'serverSide'}
                                                                data={[]}
                                                                valueKey={'zipcode'}
                                                                dataListKey={"data.docs"}
                                                                onUpdate={(value: any) => {
                                                                    handleZipCodeValueChange(value, setFieldValue)
                                                                }}
                                                                //Hiding other option from zipcode
                                                                // defaultData={[
                                                                //     {
                                                                //         zipcode: "Other"
                                                                //     }
                                                                // ]}
                                                                getOptionLabel={(option => (option.zipcode) || '')}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                                {
                                                    isZipCodeNotFound && <>
                                                        <Field name={'newZipCode'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        placeholder={"Enter New ZIP Code"}
                                                                        label={'New ZIP Code'}
                                                                        // required={true}
                                                                        maxLength={5}
                                                                        type={"text"}
                                                                        formikField={field}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                        <Field name={'state'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikAutoCompleteDropdown
                                                                        label={'State'}
                                                                        required={false}
                                                                        formikField={field}
                                                                        url={APIConfig.STATE_LIST.URL}
                                                                        method={APIConfig.STATE_LIST.METHOD}
                                                                        searchMode={'serverSide'}
                                                                        openOnFocus={true}
                                                                        dataListKey={"data.docs"}
                                                                        data={[]}
                                                                        valueKey={'state'}
                                                                        onUpdate={(value: any) => {
                                                                            setState(value?.state);
                                                                            field.form.setFieldValue("city", '');
                                                                        }}
                                                                        getOptionLabel={(option => (option.state) || '')}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                        <Field name={'city'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikAutoCompleteDropdown
                                                                        label={'City'}
                                                                        placeholder={'City'}
                                                                        required={false}
                                                                        id="city_auto_complete"
                                                                        formikField={field}
                                                                        searchMode={'serverSide'}
                                                                        url={APIConfig.CITY_LIST.URL}
                                                                        method={APIConfig.CITY_LIST.METHOD}
                                                                        valueKey={'city'}
                                                                        disabled={!state}
                                                                        payload={{
                                                                            state: state,
                                                                            page: 1,
                                                                            limit: 100
                                                                        }}
                                                                        data={[]}
                                                                        getOptionLabel={(option => (option.city) || '')}
                                                                        dataListKey={"data.docs"}
                                                                    />
                                                                )
                                                            }
                                                        </Field></>
                                                }
                                                {
                                                    !isZipCodeNotFound && values?.zipcode?.zipcode && values?.zipcode?.zipcode !== "Other" &&
                                                    <>
                                                        <Field name={'state'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        label={'State'}
                                                                        placeholder={'Enter state'}
                                                                        required={false}
                                                                        readOnly={true}
                                                                        formikField={field}
                                                                        type={"text"}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                        <Field name={'city'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        label={'City'}
                                                                        placeholder={'Enter city'}
                                                                        required={false}
                                                                        readOnly={true}
                                                                        formikField={field}
                                                                        type={"text"}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </>
                                                }
                                            </>
                                        }
                                    </>}
                                </div>
                                {isNpiFetched && <div className="drawer-actions form-action-section mrg-top-20">
                                    <ButtonComponent variant={"contained"}
                                                     fullWidth={true}
                                                     type={'submit'}
                                                     isLoading={isSubmitting}
                                                     disabled={!isValid}
                                                     color={"primary"}>
                                        Save
                                    </ButtonComponent>
                                </div>}
                            </Form>
                        )
                    }
                    }
                </Formik>
            </div>
        </div>
    );

};

export default PhysicianDetailsFormComponent;
